import React, { useState } from 'react';
import { useUserManager } from '../hooks/useUserManager';
import { 
  Users, 
  X, 
  AlertTriangle, 
  UserPlus, 
  Key, 
  Trash2, 
  Shield, 
  User, 
  Check,
  Eye,
  EyeOff,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const UserManagement = ({ dark, onClose }) => {
  const { users, loading, error, createUser, updateUser, deleteUser, clearError } = useUserManager();
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user', blockAdult: false });
  const [isFormExpanded, setIsFormExpanded] = useState(false);

  const handleCreate = async () => {
    if (!newUser.username || !newUser.password) return;
    await createUser(newUser);
    setNewUser({ username: '', password: '', role: 'user', blockAdult: false });
    // Chiudi il form dopo aver creato l'utente
    setIsFormExpanded(false);
  };

  const handleUpdate = (id, field, value) => {
    updateUser(id, { [field]: value });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="modal-container w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col rounded-2xl border border-white/10 shadow-2xl transition-all duration-200" style={{
        background: 'linear-gradient(135deg, #2a0845 0%, #6441a5 100%)',
        color: '#fff',
        backdropFilter: 'blur(16px)',
        boxShadow: '0 4px 32px #0008',
        borderRadius: 20
      }}>
        {/* Header */}
        <div className="mobile-sticky-header flex-shrink-0 mobile-reduced-padding p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center border border-blue-400/30">
                <Users size={24} className="text-blue-200" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  Gestione Utenze
                </h2>
                <p className="text-sm mt-1 text-white/70 flex items-center gap-2">
                  <User size={16} className="text-blue-300" />
                  {users.length} utenti registrati
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl transition-all duration-200 hover:bg-white/10 text-white/60 hover:text-white group"
              title="Chiudi"
            >
              <X size={20} className="group-hover:rotate-90 transition-transform duration-200" />
            </button>
          </div>
        </div>
        
        {/* New User Form - Collapsible */}
        <div className="flex-shrink-0 border-b border-white/10">
          {/* Form Header - Always Visible */}
          <div className="mobile-reduced-padding p-4 cursor-pointer hover:bg-white/5 transition-colors duration-200 tap-highlight touch-target" onClick={() => setIsFormExpanded(!isFormExpanded)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center border border-green-400/30">
                  <UserPlus size={16} className="text-green-300" />
                </div>
                <h3 className="font-semibold text-white">Crea Nuovo Utente</h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-white/60">{isFormExpanded ? 'Nascondi' : 'Mostra'}</span>
                {isFormExpanded ? (
                  <ChevronUp size={20} className="text-white/60 transition-transform duration-200" />
                ) : (
                  <ChevronDown size={20} className="text-white/60 transition-transform duration-200" />
                )}
              </div>
            </div>
          </div>
          
          {/* Form Content - Collapsible */}
          {isFormExpanded && (
            <div className="mobile-reduced-padding p-6 pt-0 animate-in slide-in-from-top-2 duration-200">
              <div className="modal-form-grid grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/70">Username</label>
                  <input 
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/30 transition-colors" 
                    placeholder="Inserisci username" 
                    value={newUser.username} 
                    onChange={e=>setNewUser({...newUser,username:e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/70">Password</label>
                  <input 
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/30 transition-colors" 
                    type="password" 
                    placeholder="Inserisci password" 
                    value={newUser.password} 
                    onChange={e=>setNewUser({...newUser,password:e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/70">Ruolo</label>
                  <select 
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/30 transition-colors"
                    value={newUser.role}
                    onChange={e=>setNewUser({...newUser,role:e.target.value})}
                  >
                    <option value="user" className="bg-gray-800">Utente</option>
                    <option value="admin" className="bg-gray-800">Admin</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/70">Controllo Contenuti</label>
                  <label className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
                    <input 
                      type="checkbox" 
                      checked={newUser.blockAdult} 
                      onChange={e=>setNewUser({...newUser,blockAdult:e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm text-white/80">Block Adult</span>
                  </label>
                </div>
                
                <button 
                  onClick={handleCreate} 
                  disabled={!newUser.username || !newUser.password}
                  className="mobile-button inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 disabled:hover:scale-100"
                >
                  <UserPlus size={16} />
                  Crea Utente
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex-shrink-0 mx-6 mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle size={20} className="text-red-400 flex-shrink-0" />
                <span className="text-red-200 font-medium">{error}</span>
              </div>
              <button
                onClick={clearError}
                className="text-red-300 hover:text-red-100 p-1 rounded-lg hover:bg-red-500/20 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto min-h-0 touch-scroll">
          <div className="mobile-table-container overflow-hidden">
            <table className="w-full">
              <thead className="sticky top-0 bg-gradient-to-r from-blue-900/50 to-purple-900/50 backdrop-blur-sm border-b border-white/10">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-blue-200">
                  <th className="p-4">
                    <div className="flex items-center gap-2">
                      <User size={14} />
                      Username
                    </div>
                  </th>
                  <th className="p-4">
                    <div className="flex items-center gap-2">
                      <Shield size={14} />
                      Ruolo
                    </div>
                  </th>
                  <th className="p-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <EyeOff size={14} />
                      Block Adult
                    </div>
                  </th>
                  <th className="p-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <X size={14} />
                      Bloccato
                    </div>
                  </th>
                  <th className="p-4">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-white/5 transition-colors duration-200 group">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center border border-blue-400/30">
                          <User size={16} className="text-blue-300" />
                        </div>
                        <span className="font-medium text-white">{u.username}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${
                        u.role === 'admin' 
                          ? 'bg-yellow-500/20 text-yellow-200 border-yellow-400/30' 
                          : 'bg-blue-500/20 text-blue-200 border-blue-400/30'
                      }`}>
                        {u.role === 'admin' ? <Shield size={12} /> : <User size={12} />}
                        {u.role === 'admin' ? 'Admin' : 'Utente'}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => handleUpdate(u.id, 'blockAdult', !u.blockAdult)}
                        className="flex items-center justify-center w-6 h-6 rounded-lg transition-all duration-200"
                      >
                        {u.blockAdult ? (
                          <EyeOff size={18} className="text-red-400" />
                        ) : (
                          <Eye size={18} className="text-gray-400 group-hover:text-white" />
                        )}
                      </button>
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => handleUpdate(u.id, 'blocked', !u.blocked)}
                        className="flex items-center justify-center w-6 h-6 rounded-lg transition-all duration-200"
                      >
                        {u.blocked ? (
                          <X size={18} className="text-red-400" />
                        ) : (
                          <Check size={18} className="text-green-400" />
                        )}
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => {
                            const pwd = prompt('Nuova password:');
                            if (pwd) handleUpdate(u.id, 'password', pwd);
                          }} 
                          className="mobile-button inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-purple-300 hover:text-purple-200 hover:bg-purple-500/20 transition-all duration-200 border border-purple-500/30 hover:border-purple-400/50"
                        >
                          <Key size={12} />
                          Reset
                        </button>
                        <button 
                          onClick={() => deleteUser(u.id)} 
                          className="mobile-button inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-red-300 hover:text-red-200 hover:bg-red-500/20 transition-all duration-200 border border-red-500/30 hover:border-red-400/50"
                        >
                          <Trash2 size={12} />
                          Elimina
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="mobile-footer flex-shrink-0 mobile-reduced-padding p-6 border-t border-white/10 bg-gradient-to-r from-blue-900/20 to-purple-900/20">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 text-sm text-white/70">
              <Users size={16} className="text-blue-400" />
              Gestione utenti e permessi dell'applicazione
            </div>
            <button
              onClick={onClose}
              className="mobile-button inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold transition-all duration-200 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl hover:scale-105"
            >
              <Check size={18} />
              Conferma
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
